// Production hedge analytics — stress matrix + delta drift + roll triggers.
//
// These pieces all read from the AggregatedGreeks output of buildPositionGreeks
// plus the strike + premium data from the chosen legs. They don't need a full
// pricing model — first-order Greek approximations are sufficient for the
// kind of ranges hedgers actually care about (spot ±15%, IV ±10pp, T+30d).
//
// For tail scenarios (e.g., spot −30%) the linear approximation breaks down;
// the UI should label those bands "directional only — pricing model required
// for exact P&L."

import type { PositionGreeks } from './greeks';
import type { HedgeLeg } from './types';

export interface StressCell {
  /** Spot offset from current (decimal, e.g. -0.10). */
  spotOffset: number;
  /** Calendar days from today the cell represents. */
  daysFromNow: number;
  /** IV-rank offset in percentage points (e.g., +30 = vol-up scenario). */
  ivShiftPp: number;
  /** Estimated $ P&L of the position at this stress cell. */
  pnlDollars: number;
}

export interface StressMatrix {
  cells: StressCell[];
  /** The set of spot offsets used (rows). */
  spotOffsets: number[];
  /** The set of (daysFromNow, ivShiftPp) used (columns). */
  columns: Array<{ daysFromNow: number; ivShiftPp: number; label: string }>;
}

/** First-order P&L approximation using delta, gamma, vega, theta.
 *
 *  ΔP&L ≈ shareDelta × ΔSpot
 *        + 0.5 × shareGamma × ΔSpot²
 *        + dollarVega × ΔIvPp
 *        + dollarTheta × Δdays
 *
 *  ΔSpot is in price units (not %), shareDelta is in share count, so the
 *  delta × ΔSpot product gives $.
 *  Gamma component is the standard quadratic correction.
 *  Vega is $ per 1pp IV move; ΔIvPp is the IV shift in percentage points.
 *  Theta is $ per day.
 */
export function estimatePnl(
  greeks: PositionGreeks,
  spotOffset: number,
  daysFromNow: number,
  ivShiftPp: number,
): number {
  const dSpot = greeks.spot * spotOffset;
  const deltaTerm = greeks.total.shareDelta * dSpot;
  const gammaTerm = 0.5 * greeks.total.shareGamma * dSpot * dSpot;
  const vegaTerm = greeks.total.dollarVega * ivShiftPp;
  const thetaTerm = greeks.total.dollarTheta * daysFromNow;
  return deltaTerm + gammaTerm + vegaTerm + thetaTerm;
}

/** Build a production stress matrix. Default grid:
 *
 *   Rows: spot -15% / -10% / -5% / 0 / +5% / +10% / +15%
 *   Cols: (T+0, IV+0)  (T+30, IV+0)  (T+0, IV+30pp)  (Texp, IV+0)
 *
 *  Caller supplies daysToExpiry for the final "to-expiration" column. */
export function buildStressMatrix(
  greeks: PositionGreeks,
  daysToExpiry: number,
  args: {
    spotOffsets?: number[];
    extraColumns?: Array<{ daysFromNow: number; ivShiftPp: number; label: string }>;
  } = {},
): StressMatrix {
  const spotOffsets = args.spotOffsets ?? [-0.15, -0.10, -0.05, 0, 0.05, 0.10, 0.15];
  const columns: StressMatrix['columns'] = [
    { daysFromNow: 0, ivShiftPp: 0, label: 'Now' },
    { daysFromNow: 30, ivShiftPp: 0, label: 'T+30d' },
    { daysFromNow: 0, ivShiftPp: 30, label: 'IV +30pp' },
    { daysFromNow: daysToExpiry, ivShiftPp: 0, label: `T+${daysToExpiry}d (expiry)` },
    ...(args.extraColumns ?? []),
  ];
  const cells: StressCell[] = [];
  for (const offset of spotOffsets) {
    for (const col of columns) {
      cells.push({
        spotOffset: offset,
        daysFromNow: col.daysFromNow,
        ivShiftPp: col.ivShiftPp,
        pnlDollars: estimatePnl(greeks, offset, col.daysFromNow, col.ivShiftPp),
      });
    }
  }
  return { cells, spotOffsets, columns };
}

// ─── Roll triggers ─────────────────────────────────────────────────────────

export type RollTriggerKey =
  | 'short_call_breach'
  | 'long_put_deep_itm'
  | 'iv_regime_change'
  | 'delta_drift';

export interface RollTrigger {
  key: RollTriggerKey;
  /** Human-readable trigger name. */
  name: string;
  /** True when the trigger condition is currently met. */
  fired: boolean;
  /** Current value of the metric being tracked. */
  value: number;
  /** Threshold above (or below) which the trigger fires. */
  threshold: number;
  /** Plain-English rationale + recommended action. */
  rationale: string;
}

/** Roll-trigger assessment for a live collar. Provides the four production
 *  trigger checks:
 *
 *    1. Short-call breach — spot has crossed 75% of (strike − current spot
 *       at entry); time to roll up-and-out before gamma + delta accelerate.
 *
 *    2. Long-put 50% ITM — put has gone significantly profitable; consider
 *       capturing the gain and resetting the floor.
 *
 *    3. IV regime change — current IV differs from entry IV by > 30%
 *       (relative). Triggers a review of which leg is now rich vs cheap.
 *
 *    4. Delta drift — net share-delta has moved > 20% of stock-delta from
 *       the target ratio.
 *
 *  Threshold values can be overridden via opts. Defaults reflect common
 *  desk practice. */
export interface RollContext {
  spot: number;
  shortCallStrike: number | null;
  longPutStrike: number | null;
  entryIv: number | null;
  currentIv: number | null;
  shareDeltaCurrent: number;
  shareDeltaTarget: number;
  stockShares: number;
}

export function assessRollTriggers(
  ctx: RollContext,
  opts: {
    shortCallBreachRatio?: number;   // default 0.75 — fraction of strike
    longPutItmRatio?: number;        // default 0.50 — fraction of put strike
    ivRegimeChangeRatio?: number;    // default 0.30 — relative IV change
    deltaDriftRatio?: number;        // default 0.20 — fraction of stock delta
  } = {},
): RollTrigger[] {
  const triggers: RollTrigger[] = [];
  const shortCallRatio = opts.shortCallBreachRatio ?? 0.75;
  const longPutItmRatio = opts.longPutItmRatio ?? 0.50;
  const ivRegimeRatio = opts.ivRegimeChangeRatio ?? 0.30;
  const deltaDriftRatio = opts.deltaDriftRatio ?? 0.20;

  // 1. Short-call breach. Trigger when spot ≥ strike × shortCallRatio + (1−ratio) × ...
  // Simpler convention: trigger when spot has covered ≥ shortCallRatio of the
  // distance from a base (we use 0) to strike. i.e., spot/strike ≥ ratio.
  if (ctx.shortCallStrike != null) {
    const value = ctx.spot / ctx.shortCallStrike;
    const fired = value >= shortCallRatio;
    triggers.push({
      key: 'short_call_breach',
      name: 'Short-call breach',
      fired,
      value,
      threshold: shortCallRatio,
      rationale: fired
        ? `Spot $${ctx.spot.toFixed(2)} is ${(value * 100).toFixed(0)}% of strike $${ctx.shortCallStrike.toFixed(2)}. Roll the call up and out before gamma accelerates near expiry.`
        : `Spot at ${(value * 100).toFixed(0)}% of strike — well under the ${(shortCallRatio * 100).toFixed(0)}% roll trigger.`,
    });
  }

  // 2. Long-put significantly ITM (put protection has gone profitable).
  if (ctx.longPutStrike != null) {
    // Trigger when spot has fallen so the put's intrinsic value covers
    // (1 − longPutItmRatio) × strike. With longPutItmRatio=0.50 default that
    // fires once the put has gone deeply ITM enough that capturing the gain
    // and resetting the floor becomes attractive.
    const intrinsic = Math.max(0, ctx.longPutStrike - ctx.spot);
    const itmFraction = intrinsic / ctx.longPutStrike;
    const fired2 = itmFraction >= (1 - longPutItmRatio);
    triggers.push({
      key: 'long_put_deep_itm',
      name: 'Long-put deeply ITM',
      fired: fired2,
      value: itmFraction,
      threshold: 1 - longPutItmRatio,
      rationale: fired2
        ? `Long put is ${(itmFraction * 100).toFixed(0)}% ITM. Consider taking the gain and resetting the floor.`
        : `Put is ${(itmFraction * 100).toFixed(0)}% ITM — under the ${((1 - longPutItmRatio) * 100).toFixed(0)}% roll trigger.`,
    });
  }

  // 3. IV regime change.
  if (ctx.entryIv != null && ctx.currentIv != null && ctx.entryIv > 0) {
    const value = Math.abs(ctx.currentIv - ctx.entryIv) / ctx.entryIv;
    const fired = value >= ivRegimeRatio;
    const direction = ctx.currentIv > ctx.entryIv ? 'higher' : 'lower';
    triggers.push({
      key: 'iv_regime_change',
      name: 'IV regime change',
      fired,
      value,
      threshold: ivRegimeRatio,
      rationale: fired
        ? `IV has moved ${(value * 100).toFixed(0)}% ${direction} vs entry. Review which leg is now rich/cheap; consider closing the side that's gotten extreme.`
        : `IV is within ${(value * 100).toFixed(0)}% of entry — under the ${(ivRegimeRatio * 100).toFixed(0)}% regime-change trigger.`,
    });
  }

  // 4. Delta drift — net delta moved >X% of stock delta from the target.
  if (ctx.stockShares > 0) {
    const drift = Math.abs(ctx.shareDeltaCurrent - ctx.shareDeltaTarget);
    const value = drift / ctx.stockShares;
    const fired = value >= deltaDriftRatio;
    triggers.push({
      key: 'delta_drift',
      name: 'Delta drift',
      fired,
      value,
      threshold: deltaDriftRatio,
      rationale: fired
        ? `Net delta has drifted ${(value * 100).toFixed(0)}% of stock-delta from the target. Rebalance the contract count.`
        : `Delta is within ${(value * 100).toFixed(0)}% of target — under the ${(deltaDriftRatio * 100).toFixed(0)}% drift trigger.`,
    });
  }

  return triggers;
}

/** Convenience helper — pull short-call + long-put strikes from legs. */
export function extractRollCriticalLegs(legs: HedgeLeg[]): {
  shortCallStrike: number | null;
  longPutStrike: number | null;
  entryIvCall: number | null;
  entryIvPut: number | null;
} {
  const call = legs.find(l => l.asset === 'call' && l.action === 'sell');
  const put = legs.find(l => l.asset === 'put' && l.action === 'buy');
  return {
    shortCallStrike: call?.strike ?? null,
    longPutStrike: put?.strike ?? null,
    entryIvCall: call?.iv ?? null,
    entryIvPut: put?.iv ?? null,
  };
}
